import { getter } from '@progress/kendo-react-common';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Input, NumericTextBox } from '@progress/kendo-react-inputs';
import { Label, Error } from "@progress/kendo-react-labels";

export const inputLabel = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, visited, label, id, valid, disabled, ...others } =
        fieldRenderProps;
    const showValidationMessage: string | false | null =
        visited && validationMessage;
    return (
        <FieldWrapper>
            <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
                {label}
            </Label>
            <Input
                valid={valid}
                id={id}
                disabled={disabled}
                {...others}
            />
            {showValidationMessage && <Error>{validationMessage}</Error>}
        </FieldWrapper>
    );
};

export const NumericInputLabel = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, visited, label, id, valid, disabled, ...others } =
        fieldRenderProps;
    const showValidationMessage: string | false | null =
        visited && validationMessage;

    return (
        <FieldWrapper style={{ width: "100%" }}>
            <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
                {label}
            </Label>
            <NumericTextBox
                valid={valid}
                id={id}
                disabled={disabled}
                spinners={false}
                step={0}
                {...others}
            />
            {showValidationMessage && <Error>{validationMessage}</Error>}
        </FieldWrapper>
    );
};

export const dateInputLabel = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, visited, label, id, valid, disabled, ...others } =
        fieldRenderProps;
    const showValidationMessage: string | false | null =
        visited && validationMessage;
    return (
        <FieldWrapper>
            <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
                {label}
            </Label>
            <DatePicker
                valid={valid}
                id={id}
                disabled={disabled}
                format={{
                    pattern: "MM/dd/yyyy"
                }}
                {...others}
            />
            {showValidationMessage && <Error>{validationMessage}</Error>}
        </FieldWrapper>
    );
};

const emailRegex: RegExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/;
const phoneNumberRegex: RegExp = /^(?:\+?1\s?)?(?:\(\d{3}\)|\d{3})[-\s]?\d{3}[-\s]?\d{4}$/;
const postalCodeRegex: RegExp = /^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] \d[A-Z]\d|[A-Z]\d[A-Z]\d)$/i;

// This is the main required validation
export const requiredValidator = (value: string) => value
    ? ""
    : "This field is required.";

export const termsValidator = (value: string) => value
    ? ""
    : "It's required to agree with Terms and Conditions.";
export const requiredEmailValidator = (value: string) => !value
    ? "Email field is required."
    : emailValidator(value);
export const emailValidator = (value: string) => (emailRegex.test(value)
    ? ""
    : "Email is not in a valid format.");
export const nameValidator = (value: string) => !value
    ? "Full Name is required"
    : value.length < 7
        ? "Full Name should be at least 7 characters long."
        : "";
export const userNameValidator = (value: string) => !value
    ? "User Name is required"
    : value.length < 5
        ? "User name should be at least 5 characters long."
        : "";
export const phoneValidator = (value: string) => !value
    ? "Phone number is required." :
    phoneNumberRegex.test(value)
        ? ""
        : "Not a valid phone number.";
export const passwordValidator = (value: string) => value && value.length > 8
    ? ''
    : 'Password must be at least 8 symbols.';
export const addressValidator = (value: string) => value
    ? ""
    : "Address is required.";
export const kitNumValidator = (value: string) => value
    ? ""
    : "Kit Number is required";
export const lotNumValidator = (value: string) => value
    ? ""
    : "Lot Number is required.";
export const postalCodeValidator = (value: string) => !value
    ? "Postal Code is required" :
    postalCodeRegex.test(value)
        ? ""
        : "Not a vaild Postal Code.";
export const nonUSStateValidator = (value: string) => !value
    ? "State is required" :
    value.length === 2
        ? ""
        : "State Abbreviation must have 2 characters.";
export const smallKitSizeValidator = (value: string) => {
    return numberRangeValidator(10, 200)(value);
}
export const largeKitSizeValidator = (value: string) => {
    return numberRangeValidator(201, 5000)(value);
}

export const numberRangeValidator = (min: number, max: number) => {
    return (value: string) => {
        var nValue = Number.parseInt(value);
        if (nValue) {
            return (min <= nValue && nValue <= max) ?
                "" :
                "Value must be between " + min + " and " + max;
        }
        return "Not a valid Number";
    }
}

const userNameGetter = getter('username');
const emailGetter = getter('email');

export const formValidator = (values: any) => {
    const userName = userNameGetter(values);
    const emailValue = emailGetter(values);

    if (userName && emailValue && emailRegex.test(emailValue)) {
        return {};
    }

    return {
        VALIDATION_SUMMARY: 'Please fill in the following fields.',
        ['username']: !userName ? 'User Name is required.' : '',
        ['email']: emailValue && emailRegex.test(emailValue) ? '' : 'Email is required and should be in a valid format.'
    };
};